import { ButtonLink, StatusIndicator } from '../../design/index.js';
import type { StatusKind } from '../../design/index.js';
import { SiteSection } from '../SiteSection.js';

/**
 * Your diary stays your diary (§21).
 *
 * The commercially important section, and the one most easily turned into a
 * lie. Every line states what is **actually** true today:
 *
 *   * Amelia — the connector is built from Amelia's official API collection
 *     and tested against its shapes, but not yet against a live site (STATUS,
 *     "Amelia on a live site"). So it is marked bêta, not "disponible".
 *   * Planity — no connector, and no reverse engineering (ADR-0015). "À
 *     venir" means an agreement or a published API, not a scraper.
 *   * No logos. Putting a partner's mark on our page implies an arrangement
 *     we do not have. A name and a sentence state the same fact and claim
 *     nothing.
 *
 * A prospect who is told "bêta" and finds it works is pleasantly surprised.
 * One who is told "disponible" and finds it does not is gone.
 */
export function Integrations(): React.JSX.Element {
  return (
    <SiteSection
      id="integrations"
      title="Votre agenda reste votre agenda."
      intro={
        <p>
          Nous ne cherchons pas à remplacer votre logiciel de réservation. GSI Voice devient la
          couche téléphonique qui dialogue avec lui.
        </p>
      }
    >
      <ul className="border-b border-wall-line">
        {CONNECTORS.map((connector) => (
          <li
            key={connector.name}
            className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2 border-t border-wall-line py-5"
          >
            <span className="min-w-0">
              <span className="block text-subtitle font-medium text-ink">{connector.name}</span>
              <span className="block text-ui-sm text-ink-muted">{connector.detail}</span>
            </span>
            <StatusIndicator kind={STATE[connector.availability].kind} pill>
              {STATE[connector.availability].label}
            </StatusIndicator>
          </li>
        ))}
      </ul>

      <p className="mt-6 max-w-2xl text-ui text-ink-muted">
        Nous n’affichons jamais comme disponible un connecteur qui ne l’est pas. Dites-nous quel
        logiciel vous utilisez : s’il n’est pas dans la liste, nous vous dirons franchement ce qu’il
        faut pour l’ajouter.
      </p>

      <div className="mt-6">
        <ButtonLink href="#demo" variant="secondary">
          Nous dire quel logiciel vous utilisez
        </ButtonLink>
      </div>
    </SiteSection>
  );
}

type Availability = 'available' | 'beta' | 'planned' | 'on_request';

/** The same four words, and the same colours, as the dashboard's `IntegrationCard`. */
const STATE: Record<Availability, { readonly kind: StatusKind; readonly label: string }> = {
  available: { kind: 'ok', label: 'Disponible' },
  beta: { kind: 'warn', label: 'Bêta' },
  planned: { kind: 'neutral', label: 'À venir' },
  on_request: { kind: 'neutral', label: 'Sur demande' },
};

const CONNECTORS: readonly {
  readonly name: string;
  readonly detail: string;
  readonly availability: Availability;
}[] = [
  {
    name: 'Amelia',
    detail: 'L’agenda en ligne de votre site WordPress.',
    availability: 'beta',
  },
  {
    name: 'Sans logiciel',
    detail: 'GSI Voice tient l’agenda pour vous, et vous le consultez dans le tableau de bord.',
    availability: 'available',
  },
  {
    name: 'Planity',
    detail: 'Seulement par un accord ou une interface publiée, jamais en contournant le service.',
    availability: 'planned',
  },
  {
    name: 'Autre logiciel',
    detail: 'Un connecteur sur mesure, si votre logiciel en offre les moyens.',
    availability: 'on_request',
  },
];
